import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const PLATFORM_HOST_HINTS = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
]);

function isLikelyPlatformHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h || PLATFORM_HOST_HINTS.has(h)) return true;
  if (h.endsWith('.railway.app') || h.endsWith('.vercel.app') || h.endsWith('.netlify.app')) {
    return true;
  }
  const frontend = (process.env.REACT_APP_PUBLIC_SITE_URL || process.env.PUBLIC_URL || '').trim();
  if (frontend) {
    try {
      const u = new URL(frontend.includes('://') ? frontend : `https://${frontend}`);
      if (u.hostname && u.hostname.toLowerCase() === h) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

/**
 * On an agency custom domain, map / and /auth to /w/:slug (and /w/:slug/auth).
 * Platform hosts are left alone.
 */
export default function WhitelabelHostGate({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ loading: true, slug: null, checked: false });

  useEffect(() => {
    let cancelled = false;
    const host = window.location.hostname;

    if (isLikelyPlatformHost(host)) {
      setState({ loading: false, slug: null, checked: true });
      return undefined;
    }

    // Already on a workspace path — no bootstrap needed
    if (location.pathname.startsWith('/w/')) {
      setState({ loading: false, slug: null, checked: true });
      return undefined;
    }

    const cached = sessionStorage.getItem(`wl_host_${host}`);
    if (cached) {
      setState({ loading: false, slug: cached, checked: true });
      return undefined;
    }

    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/whitelabel/by-host?host=${encodeURIComponent(host)}`,
        );
        if (!res.ok) {
          if (!cancelled) setState({ loading: false, slug: null, checked: true });
          return;
        }
        const data = await res.json();
        const slug = (data.slug || '').trim();
        if (slug) {
          sessionStorage.setItem(`wl_host_${host}`, slug);
        }
        if (!cancelled) setState({ loading: false, slug: slug || null, checked: true });
      } catch {
        if (!cancelled) setState({ loading: false, slug: null, checked: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!state.checked || state.loading) {
    if (!isLikelyPlatformHost(window.location.hostname) && !location.pathname.startsWith('/w/')) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          <p className="text-sm text-zinc-400">Loading workspace…</p>
        </div>
      );
    }
    return children;
  }

  if (state.slug) {
    const path = location.pathname || '/';
    if (path === '/' || path === '') {
      return <Navigate to={`/w/${state.slug}${location.search || ''}`} replace />;
    }
    if (path === '/auth' || path === '/login') {
      return <Navigate to={`/w/${state.slug}/auth${location.search || ''}`} replace />;
    }
  }

  return children;
}
